import { Button } from '@/components/ui/button';
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
} from '@/components/ui/form';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { useListOffers } from '@/features/offers/api/list-offers';
import { AlertTriangle, Tag } from 'lucide-react';
import { useMemo } from 'react';
import { useFormContext } from 'react-hook-form';

import { AD_PROMOTION_LIST_PARAMS } from '../../-hooks/use-creative-promotion-preselect';
import { type AdWizardFormData, adWizardLabels as L } from '../../-schema';
import {
  type PromotionOption,
  copyMentionsPrice,
  groupPromotionOptions,
  hasPromotion,
  promotionOptionLabel,
} from './ad-promotion-options';

/** The non-blocking nudge shown when the copy quotes a price. */
export const AD_PRICE_HINT =
  'This ad mentions a price — link the promotion so Claire quotes it correctly.';

function PromotionItem({ offer }: { offer: PromotionOption }) {
  return (
    <SelectItem value={offer.id}>{promotionOptionLabel(offer)}</SelectItem>
  );
}

/**
 * The optional "Promotion" an ad advertises, picked while creating the ad.
 *
 * Lists the org's live and draft promotions, those linked to the ad's
 * service(s) first. Claire quotes the linked promotion's price, and reports
 * attribute the ad to it; a draft is published with the ad on launch (the
 * publish dialog names it and asks first). Same list and draft warning as the
 * ad side panel's "What this ad promotes".
 *
 * Rendered on the details step, under Service. The customize step renders it
 * again when the copy quotes a price and nothing is linked yet, so the nudge
 * sits next to the control that answers it, and when a promotion is linked
 * (it may have been preselected from the creative after the details step).
 */
export function AdPromotionField() {
  const { control, watch } = useFormContext<AdWizardFormData>();
  const serviceIds = watch('serviceIds');
  const value = watch('offerId');
  const [headline, primaryText, description] = watch([
    'headline',
    'primaryText',
    'description',
  ]);

  const { offers, isLoading, isError, refetch } = useListOffers(
    AD_PROMOTION_LIST_PARAMS
  );

  const { forService, others } = useMemo(
    () =>
      groupPromotionOptions(offers, {
        serviceIds: serviceIds ?? [],
        selectedId: value,
      }),
    [offers, serviceIds, value]
  );
  const selected = hasPromotion(value)
    ? offers.find((o) => o.id === value)
    : undefined;
  const showPriceHint =
    !hasPromotion(value) &&
    copyMentionsPrice(headline, primaryText, description);
  const isEmpty = forService.length + others.length === 0;

  return (
    <FormField
      control={control}
      name="offerId"
      render={({ field }) => (
        <FormItem data-testid="ad-promotion-field">
          <FormLabel>{L.offerId}</FormLabel>

          {isLoading ? (
            <Skeleton className="h-9 w-full" aria-label="Loading promotions" />
          ) : isError ? (
            <div className="flex items-center justify-between gap-2 rounded-md border border-dashed p-2 text-sm">
              <span className="text-destructive">
                Couldn&apos;t load your promotions.
              </span>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => refetch()}
              >
                Try again
              </Button>
            </div>
          ) : isEmpty ? (
            <p className="text-sm text-muted-foreground">
              You haven&apos;t created any promotions yet.
            </p>
          ) : (
            // Untouched ('') shows the placeholder: the ad then inherits its
            // creative's promotion, which is preselected when there is one.
            <Select value={field.value ?? ''} onValueChange={field.onChange}>
              <FormControl>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Choose a promotion" />
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                {forService.length > 0 && (
                  <SelectGroup>
                    <SelectLabel>For this service</SelectLabel>
                    {forService.map((offer) => (
                      <PromotionItem key={offer.id} offer={offer} />
                    ))}
                  </SelectGroup>
                )}
                {others.length > 0 && (
                  <SelectGroup>
                    <SelectLabel>
                      {forService.length > 0
                        ? 'Other promotions'
                        : 'Your promotions'}
                    </SelectLabel>
                    {others.map((offer) => (
                      <PromotionItem key={offer.id} offer={offer} />
                    ))}
                  </SelectGroup>
                )}
              </SelectContent>
            </Select>
          )}

          {showPriceHint && (
            <FormDescription
              className="flex items-start gap-1.5"
              data-testid="ad-promotion-price-hint"
            >
              <Tag className="mt-0.5 size-3.5 shrink-0" aria-hidden />
              {AD_PRICE_HINT}
            </FormDescription>
          )}

          {selected?.state === 'draft' && (
            <div
              className="flex gap-2 rounded-md border border-amber-200/70 bg-amber-50/60 p-2 text-xs leading-relaxed text-amber-900 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-200"
              role="status"
              data-testid="ad-promotion-draft-warning"
            >
              <AlertTriangle className="mt-0.5 size-3.5 shrink-0" aria-hidden />
              <p>
                <strong>{selected.name}</strong> is still a draft. When you
                publish this ad you&apos;ll be asked to publish the promotion
                too, so clients can actually claim it.
              </p>
            </div>
          )}
        </FormItem>
      )}
    />
  );
}
